import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  X, Copy, Check, MousePointerClick, UserPlus, LogIn, Lightbulb, TrendingUp, Link2, Save, Mail, Globe, QrCode, Download, ImagePlus, Trash2, BarChart3,
} from "lucide-react";
import { api, apiErr, adminDelete } from "../lib/api";
import { SOCIAL_NETWORKS } from "../lib/socials";

const TYPE_LABELS = { blog: "Blog", site: "Site web", influenceur: "Influenceur", autre: "Autre" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fiche partenaire (modale admin) : détails, lien tracké, statistiques, réseaux sociaux
// (saisie manuelle + icônes cliquables si renseignés), logo et QR code du lien.
export default function PartnerFiche({ partner, onClose, onSaved }) {
  const [socials, setSocials] = useState(partner.socials || {});
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const link = `${window.location.origin}/r/${partner.code}`;
  const logoUrl = `${BACKEND}/api/partners/${partner.id}/logo`;
  const qrUrl = `${BACKEND}/api/partners/${partner.id}/qrcode.png`;

  useEffect(() => {
    api.get(`/admin/partners/${partner.id}/analytics`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [partner.id]);

  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/partners/${partner.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo du partenaire enregistré.");
      if (onSaved) onSaved();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    try {
      if (!await adminDelete(`/admin/partners/${partner.id}/logo`)) return;
      toast.success("Logo retiré.");
      if (onSaved) onSaved();
    } catch (err) { toast.error(apiErr(err)); }
  };

  // Bloque le scroll d'arrière-plan pendant la modale.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch { const t = document.createElement("textarea"); t.value = link; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    setCopied(true); toast.success("Lien copié."); setTimeout(() => setCopied(false), 1800);
  };

  const setSoc = (key) => (e) => setSocials({ ...socials, [key]: e.target.value });

  const saveSocials = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(socials).filter(([, v]) => (v || "").trim()));
      await api.put(`/admin/partners/${partner.id}`, { socials: clean });
      toast.success("Réseaux sociaux du partenaire enregistrés.");
      if (onSaved) onSaved();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const filled = SOCIAL_NETWORKS.filter((n) => (socials[n.key] || "").trim());

  const stats = [
    { l: "Clics", v: partner.clicks, icon: MousePointerClick, c: "text-cyan" },
    { l: "Inscrits", v: partner.signups, icon: UserPlus, c: "text-green-400" },
    { l: "Connectés", v: partner.logins, icon: LogIn, c: "text-electric" },
    { l: "Idées", v: partner.ideas, icon: Lightbulb, c: "text-amber-400" },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" data-testid="partner-fiche" onClick={onClose}>
      <div className="mx-auto my-8 w-full max-w-2xl rounded-2xl border border-white/12 bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div className="flex items-start justify-between border-b border-white/10 p-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-900 text-white">{partner.name}</h2>
              <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[10px] font-600 uppercase text-electric">{TYPE_LABELS[partner.ptype] || partner.ptype}</span>
              {partner.active
                ? <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-green-400">Actif</span>
                : <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-red-400">Inactif</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-secondary">
              <a href={`mailto:${partner.contact_email}`} className="inline-flex items-center gap-1 underline-offset-2 hover:text-cyan hover:underline" data-testid="partner-fiche-mailto"><Mail size={12} /> {partner.contact_email}</a>
              {partner.note && <span>· {partner.note}</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="partner-fiche-close" className="rounded-lg p-1.5 text-ink-tertiary transition-colors hover:bg-white/5 hover:text-white"><X size={18} /></button>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto p-5">
          {/* Lien tracké */}
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><Link2 size={15} className="text-cyan" /> Lien à diffuser</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-lg border border-white/10 bg-void px-3 py-2 font-mono text-xs text-cyan" data-testid="fiche-link">{link}</code>
              <button onClick={copy} data-testid="fiche-copy" className="inline-flex items-center gap-1.5 rounded-lg bg-cyan px-3 py-2 text-xs font-600 text-void hover:glow-cyan">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copié" : "Copier"}
              </button>
            </div>
          </div>

          {/* Logo + QR code du lien */}
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><ImagePlus size={15} className="text-cyan" /> Logo & QR code du lien</div>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              {/* Logo */}
              <div className="rounded-xl border border-white/10 bg-void p-3">
                <div className="text-[10px] uppercase tracking-wide text-ink-tertiary">Logo / photo</div>
                <div className="mt-2 flex items-center gap-3">
                  {partner.has_logo ? (
                    <img src={logoUrl} alt={partner.name} className="h-14 w-14 rounded-lg object-cover" data-testid="fiche-logo-img" />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-cyan/10 font-display text-lg font-700 text-cyan">{partner.name.slice(0, 2).toUpperCase()}</span>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-[11px] text-white transition-colors hover:border-cyan" data-testid="fiche-logo-upload">
                      <ImagePlus size={13} /> {uploadingLogo ? "Envoi…" : partner.has_logo ? "Changer" : "Ajouter"}
                      <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
                    </label>
                    {partner.has_logo && (
                      <button onClick={removeLogo} data-testid="fiche-logo-remove" className="inline-flex items-center gap-1 text-[11px] text-ink-tertiary hover:text-red-400"><Trash2 size={12} /> Retirer</button>
                    )}
                  </div>
                </div>
              </div>
              {/* QR code */}
              <div className="rounded-xl border border-white/10 bg-void p-3">
                <div className="text-[10px] uppercase tracking-wide text-ink-tertiary">QR code du lien</div>
                <div className="mt-2 flex items-center gap-3">
                  <img src={qrUrl} alt="QR code" className="h-14 w-14 rounded-lg bg-white p-1" data-testid="fiche-qr-img" />
                  <a href={qrUrl} download={`qrcode-${partner.code}.png`} data-testid="fiche-qr-download"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-[11px] text-white transition-colors hover:border-cyan">
                    <Download size={13} /> Télécharger
                  </a>
                </div>
                <p className="mt-2 text-[10px] leading-snug text-ink-tertiary">À partager en événement ou en story : le scan mène à l'inscription rattachée à ce partenaire.</p>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><TrendingUp size={15} className="text-cyan" /> Activité générée</div>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {stats.map((s) => (
                <div key={s.l} className="rounded-xl border border-white/10 bg-void p-3 text-center" data-testid={`fiche-${s.l}`}>
                  <s.icon size={16} className={`mx-auto ${s.c}`} />
                  <div className="mt-1.5 font-display text-xl font-900 text-white tabular-nums">{s.v}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-tertiary">{s.l}</div>
                </div>
              ))}
              <div className="col-span-2 rounded-xl border border-white/10 bg-void p-3 text-center sm:col-span-1">
                <div className="font-display text-xl font-900 text-cyan tabular-nums">{partner.conversion} %</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-tertiary">Conversion</div>
              </div>
            </div>
          </div>

          {/* Analyse du partenariat : entonnoir + filleuls + activité */}
          {analytics && (
            <div data-testid="fiche-analytics">
              <div className="flex items-center gap-2 text-sm font-600 text-white"><BarChart3 size={15} className="text-cyan" /> Analyse du partenariat</div>

              {/* Entonnoir avec taux de conversion entre étapes */}
              <div className="mt-3 flex flex-wrap items-stretch gap-2">
                <FunnelStep label="Clics" value={analytics.funnel.clicks} color="cyan" />
                <FunnelRate value={analytics.rates.click_to_signup} />
                <FunnelStep label="Inscrits" value={analytics.funnel.signups} color="green" />
                <FunnelRate value={analytics.rates.signup_to_login} />
                <FunnelStep label="Connectés" value={analytics.funnel.logins} color="electric" />
                <FunnelRate value={analytics.rates.signup_to_idea} />
                <FunnelStep label="Idées" value={analytics.funnel.ideas} color="amber" />
              </div>

              {/* Filleuls */}
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wide text-ink-tertiary">Membres de votre communauté inscrits via ce lien ({analytics.filleuls.length})</div>
                {analytics.filleuls.length === 0 ? (
                  <p className="mt-2 text-xs italic text-ink-tertiary">Aucun inscrit pour le moment.</p>
                ) : (
                  <div className="mt-2 max-h-48 space-y-1.5 overflow-y-auto pr-1">
                    {analytics.filleuls.map((f, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-void px-3 py-2" data-testid={`fiche-filleul-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-xs font-600 text-white">{f.name}</span>
                          <span className="shrink-0 text-[10px] text-ink-tertiary">{f.registered_at ? `inscrit le ${new Date(f.registered_at).toLocaleDateString("fr-FR")}` : ""}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-ink-secondary">
                          <span className={f.logins > 0 ? "text-green-400" : "text-ink-tertiary"}>{f.logins} connexion{f.logins > 1 ? "s" : ""}</span>
                          <span className={f.ideas > 0 ? "text-amber-300" : "text-ink-tertiary"}>{f.ideas} idée{f.ideas > 1 ? "s" : ""}</span>
                          <span className="text-ink-tertiary">{f.last_login ? `vu le ${new Date(f.last_login).toLocaleDateString("fr-FR")}` : "jamais connecté"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activité des 30 derniers jours */}
              {analytics.daily.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wide text-ink-tertiary">Activité des 30 derniers jours</div>
                  <div className="mt-2 flex h-16 items-end gap-1" data-testid="fiche-daily">
                    {analytics.daily.map((d) => {
                      const max = Math.max(...analytics.daily.map((x) => x.clicks + x.signups), 1);
                      const h = Math.max(8, Math.round(((d.clicks + d.signups) / max) * 100));
                      return (
                        <div key={d.day} title={`${d.day} — ${d.clicks} clic(s), ${d.signups} inscrit(s)`}
                          className="flex-1 rounded-t bg-cyan/60 transition-colors hover:bg-cyan" style={{ height: `${h}%` }} />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Réseaux sociaux : icônes cliquables si renseignés */}
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><Globe size={15} className="text-cyan" /> Réseaux sociaux du partenaire</div>
            {filled.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="fiche-socials-active">
                {filled.map((n) => (
                  <a key={n.key} href={socials[n.key]} target="_blank" rel="noopener noreferrer" title={n.label} data-testid={`fiche-social-${n.key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan/30 bg-cyan/10 text-cyan transition-all hover:border-cyan hover:glow-cyan">
                    <n.icon size={16} />
                  </a>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-ink-tertiary">Renseignez les URL manuellement ; seuls les réseaux remplis s'affichent et sont cliquables.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {SOCIAL_NETWORKS.map((n) => (
                <label key={n.key} className="flex items-center gap-2 rounded-lg border border-white/10 bg-void px-3 py-2">
                  <n.icon size={14} className={(socials[n.key] || "").trim() ? "text-cyan" : "text-ink-tertiary"} />
                  <input value={socials[n.key] || ""} onChange={setSoc(n.key)} placeholder={n.label} data-testid={`fiche-input-${n.key}`}
                    className="w-full bg-transparent text-xs text-white outline-none placeholder:text-ink-tertiary" />
                </label>
              ))}
            </div>
            <button onClick={saveSocials} disabled={saving} data-testid="fiche-save-socials"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
              <Save size={15} /> {saving ? "Enregistrement…" : "Enregistrer les réseaux"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const FUNNEL_COLORS = {
  cyan: "border-cyan/30 bg-cyan/10 text-cyan",
  green: "border-green-400/30 bg-green-400/10 text-green-400",
  electric: "border-electric/30 bg-electric/10 text-electric",
  amber: "border-amber-400/30 bg-amber-400/10 text-amber-300",
};

function FunnelStep({ label, value, color }) {
  return (
    <div className={`flex min-w-[70px] flex-col items-center justify-center rounded-xl border px-3 py-2 ${FUNNEL_COLORS[color] || FUNNEL_COLORS.cyan}`}>
      <div className="font-display text-lg font-900 tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function FunnelRate({ value }) {
  return (
    <div className="flex items-center justify-center self-center rounded-full border border-white/10 bg-void px-2 py-1 text-[10px] font-600 text-ink-secondary" title="Taux de conversion vers l'étape suivante">
      → {value}%
    </div>
  );
}
